
import { useState, useEffect } from "react";
import { Search, X, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { getCategories } from "@/services/promptService";
import type { Category, Prompt } from "@/services/promptService";

interface PromptSelectorProps {
  onPromptSelect: (promptId: string) => void;
  selectedPrompt: string | null;
}

const PromptSelector = ({ onPromptSelect, selectedPrompt }: PromptSelectorProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [filteredPrompts, setFilteredPrompts] = useState<Prompt[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchPrompts();
    loadCategories();
  }, []);

  useEffect(() => {
    filterPrompts();
  }, [searchQuery, selectedCategory, prompts]);

  const fetchPrompts = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('prompts')
        .select(`
          id, 
          title, 
          description, 
          content,
          likes,
          reviews,
          copies,
          created_at,
          user_id,
          category_id,
          categories(id, name, slug, description, icon, parent_id)
        `)
        .eq('status', 'approved');

      if (error) throw error;
      
      if (!data || data.length === 0) {
        setPrompts([]);
        return;
      }

      const promptsWithData = data.map(item => ({
        id: item.id,
        title: item.title,
        description: item.description,
        content: item.content,
        category: item.categories?.name || 'Uncategorized',
        categorySlug: item.categories?.slug,
        likes: item.likes || 0,
        reviews: item.reviews || 0,
        copies: item.copies || 0,
        author: {
          id: item.user_id || 'anonymous',
          name: 'Anonymous'
        },
        createdAt: item.created_at
      }));

      setPrompts(promptsWithData);
      setFilteredPrompts(promptsWithData);
    } catch (error: any) {
      console.error('Error fetching prompts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const cats = await getCategories();
      setCategories(cats);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const filterPrompts = () => {
    let filtered = prompts;
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        prompt => 
          prompt.title.toLowerCase().includes(query) || 
          prompt.description.toLowerCase().includes(query)
      );
    }
    
    if (selectedCategory) {
      filtered = filtered.filter(
        prompt => prompt.categorySlug === selectedCategory
      );
    }
    
    setFilteredPrompts(filtered);
  };

  const handleCategoryClick = (categoryId: string) => {
    if (selectedCategory === categoryId) {
      setSelectedCategory("");
    } else {
      setSelectedCategory(categoryId);
    }
  };

  const clearFilters = () => {
    setSearchQuery("");
    setSelectedCategory("");
  };

  return (
    <div className="h-full flex flex-col">
      <div className="mb-4">
        <div className="relative">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search prompts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-8"
          />
          {(searchQuery || selectedCategory) && (
            <button
              className="absolute right-2 top-2.5 text-muted-foreground hover:text-foreground"
              onClick={clearFilters}
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
      
      <div className="mb-4">
        <ScrollArea className="max-h-24">
          <div className="flex flex-wrap gap-2">
            {categories.map(category => (
              <Badge
                key={category.id}
                variant={selectedCategory === category.slug ? "default" : "outline"}
                className="px-3 py-1 cursor-pointer"
                onClick={() => handleCategoryClick(category.slug)}
              >
                {category.name}
              </Badge>
            ))}
          </div>
        </ScrollArea>
      </div>
      
      <div className="flex-1 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <ScrollArea className="h-[400px]">
            {filteredPrompts.length > 0 ? (
              <div className="space-y-2">
                {filteredPrompts.map(prompt => (
                  <div
                    key={prompt.id}
                    className={`p-3 rounded-md cursor-pointer transition-colors ${
                      selectedPrompt === prompt.id
                        ? "bg-primary/10 border border-primary/20"
                        : "hover:bg-muted border border-transparent"
                    }`}
                    onClick={() => onPromptSelect(prompt.id)}
                  >
                    <h3 className="font-medium truncate">{prompt.title}</h3>
                    <p className="text-sm text-muted-foreground truncate">
                      {prompt.description}
                    </p>
                    <div className="flex items-center mt-2">
                      <Badge variant="outline" className="text-xs">
                        {prompt.category}
                      </Badge>
                      <span className="text-xs text-muted-foreground ml-auto">
                        {new Date(prompt.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full p-4 text-center text-muted-foreground">
                <p>No prompts found matching your criteria.</p>
                <button
                  className="text-primary text-sm mt-2 hover:underline"
                  onClick={clearFilters}
                >
                  Clear filters
                </button>
              </div>
            )}
          </ScrollArea>
        )}
      </div>
    </div>
  );
};

export default PromptSelector;
